// React
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import {
  fetchYards,
  useYardStore,
} from "../../../../store/oasis/yardsStore";

// Components
import MenuButton from "./components/MenuButton";
import { isMenuItemEnabled } from "../../../../config/appsConfig";

// Librerias externas
import { useTranslation } from "react-i18next";
import Select from "react-select";

// Estilos
import {
  ContainerSideMenu,
  Menu,
  ContainerMenuButtons,
  SubmenuTitle,
  ContainerButtons,
  SelectStyles,
} from "./styles";

// Tipados
interface SideMenuProps {
  view: string;
  setView: (view: string) => void;
  expandableMenu: boolean;
  setExpandableMenu: (value: boolean) => void;
  isDarkMode?: boolean;
}

const defaultOption = { value: "", label: "Seleccione un patio" };

function SideMenu({
  view,
  expandableMenu,
  setExpandableMenu,
  isDarkMode = false,
}: SideMenuProps) {
  // Hooks
  const { t } = useTranslation("sideMenu");
  const navigate = useNavigate();

  // Estadod global
  const { setYardSelected } = useYardStore();

  // Estados locales
  const [timer, setTimer] = useState<NodeJS.Timeout | null>(null);
  const [selectedYard, setSelectedYard] = useState(defaultOption);
  const [yardOptions, setYardOptions] = useState([defaultOption]);
  const [yards, setYards] = useState<any[]>([]);

  // FUNCIONES

  // Maneja el cambio de menu
  const handleMenuNavigate = (newView: string) => {
    navigate(`/oasis/${newView}`);
    localStorage.setItem("view", newView);
  };

  // Expandir menu al pasar el mouse
  const handleMouseEnter = () => {
    if (timer) {
      clearTimeout(timer);
      setTimer(null);
    }
    setExpandableMenu(true);
  };

  // Contraer menu al salir el mouse
  const handleMouseLeave = () => {
    const id = setTimeout(() => {
      setExpandableMenu(false);
    }, 1000);
    setTimer(id);
  };

  // Maneja el cambio de patio seleccionado
  const handleSeledtedYards = (option: (typeof yardOptions)[0] | null) => {
    setSelectedYard(option || yardOptions[0]);
    // Buscar el objeto completo en el array yards por el id seleccionado
    const selectedObj = yards.find((yard) => yard.id === (option?.value ?? ""));
    setYardSelected(selectedObj || null);
  };

  // EFECTOS

  // Cargar patios al montar el componente
  useEffect(() => {
    fetchYards()
      .then((yards: any[]) => {
        const opts = [defaultOption].concat(
          yards.map((yard) => ({
            value: yard.id || yard._id || yard.value || "", // Ajusta según tu modelo
            label: yard.name || yard.label || yard.nombre || "Patio",
          }))
        );

        setYards(yards);
        setYardOptions(opts);

        // Si hay un yard guardado en localStorage, seleccionarlo en el select
        const stored = localStorage.getItem("yardSelected");
        if (stored) {
          const storedYard = JSON.parse(stored);
          // Buscar la opción correspondiente en yardOptions
          const foundOption = opts.find((opt) => opt.value === storedYard.id);
          if (foundOption) {
            setSelectedYard(foundOption);
          }
        }
      })
      .catch(() => setYardOptions([defaultOption]));
  }, []);

  return (
    <ContainerSideMenu
      className={
        isDarkMode
          ? "bg-[#232323] border-dark-border"
          : "bg-white border-gray-200"
      }
    >
      <Menu
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        aria-label="Sidebar"
        className={`${expandableMenu ? "w-64" : "w-fit items-center"} ${
          isDarkMode ? "bg-dark-surface text-white " : "text-gray-900 "
        }`}
      >
        <>
          <ContainerButtons
            className={`${expandableMenu ? "px-3" : "items-center"}`}
          >
            {isMenuItemEnabled("rastreo-telemetria", "oasis", "yards") && (
              <MenuButton
                id="residentials-section"
                view={"truckYards"}
                currentView={view}
                handleClick={() => handleMenuNavigate("yards")}
                expandableMenu={expandableMenu}
                validView={["yards", "yardForm"]}
                title={"yards"}
                t={t}
              />
            )}
          </ContainerButtons>
          <hr
            className={
              isDarkMode ? "border-dark-border my-5" : "border-gray-200 my-5"
            }
          />
        </>
        <ContainerMenuButtons>
          <div className="w-full px-3 h-auto">
            {expandableMenu && <SubmenuTitle>Catálogos</SubmenuTitle>}
            <ul className="space-y-2 font-medium w-auto h-auto flex flex-col gap-2">
              {expandableMenu && (
                <li id="menu-select-residential">
                  <Select
                    value={selectedYard}
                    options={yardOptions}
                    // isDisabled={selectedYard.value === ""}
                    placeholder={t("SelectPatio")}
                    styles={SelectStyles(isDarkMode)}
                    isSearchable={false}
                    onChange={handleSeledtedYards}
                    noOptionsMessage={() => t("withoutTruckYards")}
                  />
                </li>
              )}
              <div
                id="menu-sections"
                className={`${isDarkMode ? "" : "bg-white"} w-auto rounded-md`}
              >
                {/* <li>
                  <MenuButton
                    view="telemetry"
                    currentView={view}
                    expandableMenu={expandableMenu}
                    validView={[
                      "telemetry",
                      "telemetry/drivers",
                      "telemetry/trucks",
                      "telemetry/shipment",
                    ]}
                    options={[
                      {
                        section: "telemetry",
                        value: "drivers",
                        label: "drivers",
                        action: () => handleMenuView("telemetry/drivers"),
                      },
                      {
                        section: "telemetry",
                        value: "trucks",
                        label: "trucks",
                        action: () => handleMenuView("telemetry/trucks"),
                      },
                      {
                        section: "telemetry",
                        value: "shipment",
                        label: "shipment",
                        action: () => handleMenuView("telemetry/shipment"),
                      },
                    ]}
                    title={"telemetry"}
                    t={t}
                    submenu={true}
                    handleClick={handleMenuView}
                    darkMode={isDarkMode}
                  />
                </li> */}
                {isMenuItemEnabled("rastreo-telemetria", "oasis", "platformArea") && <li>
                  <MenuButton
                    view="platformArea"
                    currentView={view}
                    expandableMenu={expandableMenu}
                    validView={["platformArea"]}
                    options={[
                      {
                        section: "platformArea",
                        value: "parkingLots",
                        label: "parkingLots",
                        action: () =>
                          handleMenuNavigate("platformArea/parkingLots")
                      },
                    ]}
                    title={"platformArea"}
                    submenu={true}
                    t={t}
                    handleClick={handleMenuNavigate}
                    darkMode={isDarkMode}
                    disabled={selectedYard.value === ""}
                  />
                </li>}
                {isMenuItemEnabled("rastreo-telemetria", "oasis", "security") && <li>
                  <MenuButton
                    view="security"
                    currentView={view}
                    expandableMenu={expandableMenu}
                    validView={[
                      "security",
                      "security/access",
                      "security/guards",
                      "security/guardForm",
                      "security/announcementsGuards",
                      "security/announcementGuardForm",
                    ]}
                    options={[
                      {
                        section: "security",
                        value: "access",
                        label: "access",
                        action: () => handleMenuNavigate("security/access"),
                      },
                      {
                        section: "security",
                        value: "guards",
                        label: "guards",
                        action: () => handleMenuNavigate("security/guards"),
                      },
                      {
                        section: "security",
                        value: "announcementsGuards",
                        label: "announcementsGuards",
                        action: () =>
                          handleMenuNavigate("security/announcementsGuards"),
                      },
                    ]}
                    title="security"
                    submenu={true}
                    t={t}
                    handleClick={handleMenuNavigate}
                    darkMode={isDarkMode}
                    disabled={selectedYard.value === ""}
                  />
                </li>}
              </div>
            </ul>
          </div>
        </ContainerMenuButtons>
      </Menu>
    </ContainerSideMenu>
  );
}

export default SideMenu;
